import { createContext, FC, ReactNode, useContext } from 'react';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import { profileGroupPageToPageData } from 'app/modules/analytics/utils/profileGroupPageToPageData';
import type { PageData, ProfileGroupPage } from 'types/Page';

/** Positional: sibling platform sections hold different scopes at once. */
export interface AnalyticsScope {
  pageIds: number[];
  pages: PageData[];
  primaryPage: PageData;
}

const AnalyticsScopeContext = createContext<AnalyticsScope | null>(null);

export function useAnalyticsScope(): AnalyticsScope | null {
  return useContext(AnalyticsScopeContext);
}

interface AnalyticsScopeProviderProps {
  pageIds: number[];
  pageSources: ProfileGroupPage[];
  children: ReactNode;
}

export const AnalyticsScopeProvider: FC<AnalyticsScopeProviderProps> = ({
  pageIds,
  pageSources,
  children,
}) => {
  // Not memoised: keying on the object missed every render, keying on the id froze stale page data.
  const pages = pageSources.map(profileGroupPageToPageData);
  const value: AnalyticsScope = {
    pageIds,
    pages,
    primaryPage: pages[0]!,
  };

  return (
    <AnalyticsScopeContext.Provider value={value}>
      {children}
    </AnalyticsScopeContext.Provider>
  );
};

export interface AnalyticsSubtreePage {
  page: PageData | null;
  pages: PageData[];
  isPlatformScope: boolean;
  isGroupSelected: boolean;
}

/** Inside a scope a subtree renders as if a single page were selected. */
export function useAnalyticsSubtreePage(): AnalyticsSubtreePage {
  const scope = useAnalyticsScope();
  const { selectedPage, isGroupSelected } = useAnalyticsSelection();

  if (scope) {
    return {
      page: scope.primaryPage,
      pages: scope.pages,
      isPlatformScope: true,
      isGroupSelected: false,
    };
  }

  return {
    page: selectedPage,
    pages: selectedPage ? [selectedPage] : [],
    isPlatformScope: false,
    isGroupSelected,
  };
}
